'use client';
import styles from './upcoming.module.sass';
import cn from 'classnames';
import { Html5Video, Html5VideoProps } from '@intigral/shared/components/Html5Video';
import { Text, ImageContainer, Skeleton, FlexWrapper, FontIcon, HeroBlock } from '@intigral/shared';
import { MovieButtons, MovieButtonsProps } from '../MovieButtons';
import {
    Top1Filled,
    Top2Filled,
    Top3Filled,
    Top4Filled,
    Top5Filled,
    Top6Filled,
    Top7Filled,
    Top8Filled,
    Top9Filled,
    Top10Filled,
} from '@intigral/shared';
import React, { useState } from 'react';
import { FontIconName } from '@intigral/shared/components/FontIcon';

interface BaseProps extends React.HTMLAttributes<HTMLDivElement> {
    isLoading?: boolean;
}

interface LoadingProps extends BaseProps {
    id?: string;
    isLoading: true;
    html5video?: Html5VideoProps;
    video?: React.ReactElement;
    poster?: string;
    when?: string;
    title?: string; // Optional when loading
    provider?: string;
    text?: string;
    ctas?: MovieButtonsProps;
    locked?: boolean;
    reminder?: boolean;
    top10index?: number;
    activeItem?: string | null;
    setActiveItem?: React.Dispatch<React.SetStateAction<string | null>>;
}

interface LoadedProps extends BaseProps {
    id: string;
    isLoading?: false;
    html5video?: Html5VideoProps;
    video?: React.ReactElement;
    poster?: string;
    when: string;
    title: string; // Required when loaded
    provider?: string;
    text?: string;
    ctas?: MovieButtonsProps;
    locked?: boolean;
    reminder?: boolean;
    top10index?: number;
    activeItem?: string | null;
    setActiveItem?: React.Dispatch<React.SetStateAction<string | null>>;
}

export type UpcomingItemProps = LoadingProps | LoadedProps;

const TopIcons = [
    Top1Filled,
    Top2Filled,
    Top3Filled,
    Top4Filled,
    Top5Filled,
    Top6Filled,
    Top7Filled,
    Top8Filled,
    Top9Filled,
    Top10Filled,
];

export const UpcomingItem = ({
    when,
    title,
    text,
    html5video,
    video,
    poster,
    provider,
    ctas,
    locked,
    reminder,
    top10index,
    isLoading,
    activeItem = null,
    setActiveItem = () => null,
    ...props
}: UpcomingItemProps) => {
    if (isLoading) {
        return (
            <div {...props} className={cn(styles.upcoming, props.className)}>
                <Skeleton style={{ paddingTop: '56.25%' }} />
                <div className={styles.frame}>
                    <Skeleton variant="rounded" width="32%" />
                    <Skeleton variant="rounded" width="72%" height={32} />
                    <Skeleton variant="rounded" width={100} height={32} />
                    <Skeleton variant="rounded" height={12} />
                    <Skeleton variant="rounded" height={12} />
                    <Skeleton variant="rounded" height={12} />
                    <FlexWrapper flexDirection="row" gap="2x" style={{ width: '100%' }}>
                        <Skeleton variant="rounded" height={48} width="100%" />
                        <Skeleton variant="rounded" height={48} width={48} />
                    </FlexWrapper>
                </div>
            </div>
        );
    }

    const onPlayTrailerHandler = () => {
        if (setActiveItem) setActiveItem(props?.id || null);
    };

    return (
        <div {...props} className={cn(styles.upcoming, props.className)}>
            {html5video && <Html5Video {...html5video} />}
            <div className={styles.video}>
                {activeItem === props.id && video ? (
                    video
                ) : poster ? (
                    <>
                        {video && (
                            <span onClick={onPlayTrailerHandler}>
                                <FontIcon name="play" md="x-large" size="x-large" className={styles['play-icon']} />
                            </span>
                        )}
                        <img src={poster} className={styles.poster} alt={`Upcoming ${title}`} />
                    </>
                ) : null}
            </div>
            <div className={styles.frame}>
                {when && <span className={styles.when}>{when}</span>}
                {title && (
                    <Text
                        as="h2"
                        variant="h2"
                        margin="0"
                        alignment="center"
                        size={{ default: '32', md: '36' }}
                        fontWeight="bold"
                    >
                        {title}
                    </Text>
                )}
                {provider && (
                    <div className={styles.provider}>
                        <ImageContainer src={provider} />
                    </div>
                )}
                {text && (
                    <Text as="p" alignment="start" size={{ default: '12', md: '14' }} className={styles.text}>
                        {text}
                    </Text>
                )}
                {ctas && <MovieButtons {...ctas} className={styles.buttons} />}
                {top10index !== undefined &&
                    (TopIcons[top10index - 1] ? (
                        <div className={styles.number}>{React.createElement(TopIcons[top10index - 1])}</div>
                    ) : null)}
            </div>
            {locked && (
                <div className={styles.locked}>
                    <FontIcon name="lock" size="x-small" md="smaller" />
                </div>
            )}
            {reminder && (
                <div className={styles.remind}>
                    <FontIcon name="bell" size="x-small" md="smaller" />
                </div>
            )}
        </div>
    );
};
